import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Copy, FileText, MessageCircle, Send } from "lucide-react";
import { ROLE_HEADER, VOLUNTEER_NAME_PLACEHOLDER } from "@mubin/contracts";
import type {
  MessageVolunteersResponse,
  PublicVolunteerResponse,
  VolunteerApplication,
  VolunteerApplicationsResponse,
  VolunteerStatus,
} from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, Tabs, useSubmit } from "../components/ui";
import { CreateUserModal } from "../components/user-forms";
import { InviteModal } from "../components/interview-invite";
import { clock, hijriDate } from "../lib/interview";
import { api, errorText, getStoredRole, send } from "../lib/api";
import { personGenderLabel } from "../lib/labels";
import { useMe } from "../lib/session";
import { experienceLabel, timeLabel, volunteerStatusLabel } from "../lib/volunteers";

/** Downloads the CV file attached to an application (with the active role, like other requests). */
async function downloadCv(a: VolunteerApplication) {
  const role = getStoredRole();
  const res = await fetch(`/v1/volunteer-applications/${a.id}/cv`, {
    credentials: "same-origin",
    headers: role ? { [ROLE_HEADER]: role } : {},
  });
  if (!res.ok) return;
  const disposition = res.headers.get("content-disposition") ?? "";
  const name = decodeURIComponent(disposition.match(/filename\*=UTF-8''([^;]+)/)?.[1] ?? `cv-${a.name}`);
  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

type Decision = { application: VolunteerApplication; status: "interview" | "rejected" };

/** Volunteer teacher applications: review, interview, accept with a teacher account, or decline. */
export function VolunteersPage() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [status, setStatus] = useState<VolunteerStatus>("new");
  const [accepting, setAccepting] = useState<VolunteerApplication | null>(null);
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const [copied, setCopied] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [messaging, setMessaging] = useState(false);
  const [inviting, setInviting] = useState(false);
  const list = useQuery({
    queryKey: ["volunteers", me?.activeRole, status],
    queryFn: () => api<VolunteerApplicationsResponse>(`/v1/volunteer-applications?status=${status}`),
  });
  const surahs = useQuery({
    queryKey: ["public-volunteer"],
    queryFn: () => api<PublicVolunteerResponse>("/v1/public/volunteer"),
  });
  const surahName = (id: number) => surahs.data?.surahs.find((s) => s.id === id)?.name ?? String(id);
  const formUrl = `${window.location.origin}/volunteer`;
  const shown = list.data?.applications ?? [];
  const chosen = shown.filter((a) => picked.has(a.id));
  const allPicked = shown.length > 0 && chosen.length === shown.length;
  const pick = (id: string, on: boolean) =>
    setPicked((p) => {
      const next = new Set(p);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <>
      <PageTitle
        title="طلبات التطوع"
        sub="يقدّمها المعلمون المتطوعون من نموذج التطوع العام. القبول يُنشئ حساب معلم."
        actions={
          <button
            className="btn btn-secondary"
            onClick={() => void navigator.clipboard?.writeText(formUrl).then(() => setCopied(true))}
          >
            <Copy size={16} strokeWidth={1.75} /> {copied ? "تم نسخ الرابط" : "نسخ رابط النموذج"}
          </button>
        }
      />
      <section className="card">
        <div className="toolbar">
          <Tabs
            label="حالة الطلب"
            value={status}
            onChange={(s) => {
              setStatus(s);
              setPicked(new Set());
            }}
            items={(["new", "interview", "accepted", "rejected"] as const).map((s) => ({
              value: s,
              label: volunteerStatusLabel[s],
            }))}
          />
          {(status === "new" || status === "interview") && (
            <button
              className="btn btn-secondary"
              disabled={chosen.length === 0}
              onClick={() => setInviting(true)}
              title={chosen.length ? undefined : "حدّد المتقدمين أولًا"}
            >
              <CalendarClock size={16} strokeWidth={1.75} /> رابط حجز المقابلة
              {chosen.length > 0 && ` (${chosen.length})`}
            </button>
          )}
          <button
            className="btn btn-primary"
            disabled={chosen.length === 0}
            onClick={() => setMessaging(true)}
            title={chosen.length ? undefined : "حدّد المتقدمين أولًا"}
          >
            <Send size={16} strokeWidth={1.75} /> رسالة واتساب
            {chosen.length > 0 && ` (${chosen.length})`}
          </button>
        </div>
        {list.isPending ? (
          <Loading />
        ) : list.isError ? (
          <Empty title="تعذر التحميل">{errorText(list.error)}</Empty>
        ) : list.data.applications.length === 0 ? (
          <Empty title="لا توجد طلبات">
            {status === "new" ? `شارك رابط النموذج: ${formUrl}` : undefined}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "1%" }}>
                    <input
                      type="checkbox"
                      aria-label="تحديد الكل"
                      checked={allPicked}
                      onChange={(e) => setPicked(new Set(e.target.checked ? shown.map((a) => a.id) : []))}
                    />
                  </th>
                  <th>المتقدم</th>
                  <th>الحفظ</th>
                  <th>الأوقات</th>
                  <th>التواصل</th>
                  <th>تاريخ الطلب</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.applications.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`تحديد ${a.name}`}
                        checked={picked.has(a.id)}
                        onChange={(e) => pick(a.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <b>{a.name}</b>{" "}
                      <span className="muted small">
                        · {personGenderLabel[a.gender]} · {a.nationality}
                        {a.city && ` · ${a.city}`}
                      </span>
                      <div className="muted small">
                        الهوية: <span className="ltr">{a.nationalId}</span>
                        {a.experience && ` · الخبرة: ${experienceLabel[a.experience]}`}
                      </div>
                      {a.note && <div className="muted small">{a.note}</div>}
                      {(a.hasCv || a.cvUrl) && (
                        <div className="small">
                          <FileText size={14} strokeWidth={1.75} /> السيرة الذاتية:{" "}
                          {a.hasCv && (
                            <button type="button" className="link" onClick={() => void downloadCv(a)}>
                              تنزيل الملف
                            </button>
                          )}
                          {a.hasCv && a.cvUrl && " · "}
                          {a.cvUrl && (
                            <a href={a.cvUrl} target="_blank" rel="noreferrer noopener">
                              فتح الرابط
                            </a>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <b className="num">{a.juz}</b> جزء
                      <div className="muted small">
                        من {surahName(a.fromSurah)} إلى {surahName(a.toSurah)}
                      </div>
                      {a.hasIjazah && <Badge tone="success">لديه إجازة</Badge>}
                    </td>
                    <td className="small">
                      {a.times.map((t) => (
                        <div key={t}>{t === "other" ? (a.timesOther ?? timeLabel.other) : timeLabel[t]}</div>
                      ))}
                    </td>
                    <td>
                      <a
                        className="btn btn-ghost btn-sm"
                        href={`https://wa.me/${a.phone.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <MessageCircle size={16} strokeWidth={1.75} /> <span className="ltr">{a.phone}</span>
                      </a>
                      {a.email && (
                        <div className="small">
                          <a className="ltr" href={`mailto:${a.email}`}>
                            {a.email}
                          </a>
                        </div>
                      )}
                      {a.interview && (
                        <div className="small" style={{ color: "#7a6136" }}>
                          {interviewText(a.interview)}
                        </div>
                      )}
                      {a.messagedAt && (
                        <div className="muted small">
                          أُرسلت رسالة <span className="num">{a.messagedAt.slice(0, 10)}</span>
                        </div>
                      )}
                    </td>
                    <td className="num small">{a.createdAt.slice(0, 10)}</td>
                    <td className="actions">
                      {a.status === "new" || a.status === "interview" ? (
                        <div className="row-actions">
                          <button className="btn btn-primary btn-sm" onClick={() => setAccepting(a)}>
                            قبول
                          </button>
                          {a.status === "new" && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setDeciding({ application: a, status: "interview" })}
                            >
                              مقابلة
                            </button>
                          )}
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setDeciding({ application: a, status: "rejected" })}
                          >
                            اعتذار
                          </button>
                        </div>
                      ) : (
                        <Badge tone={a.status === "accepted" ? "success" : "neutral"}>
                          {volunteerStatusLabel[a.status]}
                        </Badge>
                      )}
                      {a.decisionNote && <div className="muted small">{a.decisionNote}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <CreateUserModal
        open={accepting !== null}
        onClose={() => setAccepting(null)}
        role="teacher"
        prefill={accepting ?? undefined}
        onCreated={async (userId) => {
          if (!accepting) return;
          await send("POST", `/v1/volunteer-applications/${accepting.id}/decide`, {
            status: "accepted",
            userId,
          });
          await qc.invalidateQueries({ queryKey: ["volunteers"] });
        }}
      />
      <DecideModal decision={deciding} onClose={() => setDeciding(null)} />
      <InviteModal
        open={inviting}
        to={chosen.map((a) => ({ id: a.id, name: a.name }))}
        onClose={() => setInviting(false)}
        onSent={(ids) =>
          setPicked((p) => {
            const next = new Set(p);
            for (const id of ids) next.delete(id);
            return next;
          })
        }
      />
      <MessageModal
        open={messaging}
        to={chosen}
        status={status}
        onClose={() => setMessaging(false)}
        onSent={(ids) =>
          setPicked((p) => {
            const next = new Set(p);
            for (const id of ids) next.delete(id);
            return next;
          })
        }
      />
    </>
  );
}

const TZ = "Asia/Riyadh";
/** Where the applicant's interview stands. */
function interviewText(iv: NonNullable<VolunteerApplication["interview"]>) {
  switch (iv.status) {
    case "invited":
      return Date.parse(iv.bookBy) > Date.now()
        ? `أُرسل رابط حجز المقابلة · صالح حتى ${hijriDate(iv.bookBy, TZ)}`
        : "انتهى رابط حجز المقابلة دون حجز";
    case "booked":
      return `المقابلة: ${hijriDate(iv.startsAt!, TZ)} · ${clock(iv.startsAt!, TZ)}`;
    case "finished":
      return "أُجريت المقابلة";
    case "missed":
      return "لم يحضر المقابلة";
    case "cancelled":
      return "أُلغي موعد المقابلة";
  }
}

const GROUP_LINK = "[رابط القروب]";

/** The acceptance message (owner request 2026-11-01): they hear of it here first, with the teachers' group. */
const acceptanceText = (origin: string) =>
  `السلام عليكم ورحمة الله وبركاته
حياك الله أ. ${VOLUNTEER_NAME_PLACEHOLDER}

يسعدنا إبلاغك بقبولك معلمًا متطوعًا في الجمعية الخيرية لتحفيظ القرآن الكريم بمحافظة رماح، عبر تطبيق *مُبين* 🌿
جزاك الله خيرًا على حرصك على تعليم كتاب الله.

*للبدء:*
١. حمّل التطبيق: ${origin}/download
٢. ادخل برقم جوالك هذا، ويصلك رمز الدخول على الواتساب.

التطبيق جديد ونطوّره أولًا بأول، لذلك أنشأنا قروبًا مؤقتًا للمعلمين خلال فترة التجربة، للملاحظات وأسئلة الاستخدام وإعلان التحديثات. الانضمام اختياري:
${GROUP_LINK}

نسأل الله أن يبارك في وقتك وجهدك.`;

const greetingText = `السلام عليكم ورحمة الله وبركاته
حياك الله أ. ${VOLUNTEER_NAME_PLACEHOLDER}

`;

/** A WhatsApp message from the organization's number to the chosen applicants, each with their own name. */
function MessageModal({
  open,
  to,
  status,
  onClose,
  onSent,
}: {
  open: boolean;
  to: VolunteerApplication[];
  status: VolunteerStatus;
  onClose: () => void;
  onSent: (ids: string[]) => void;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ name: string; error: string }[] | null>(null);
  const body = text ?? (status === "accepted" ? acceptanceText(window.location.origin) : greetingText);
  const missingLink = body.includes(GROUP_LINK);
  const close = () => {
    setFailed(null);
    onClose();
  };
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      const res = await send<MessageVolunteersResponse>("POST", "/v1/volunteer-applications/message", {
        ids: to.map((a) => a.id),
        body,
      });
      await qc.invalidateQueries({ queryKey: ["volunteers"] });
      onSent(res.results.filter((r) => r.ok).map((r) => r.id));
      const bad = res.results
        .filter((r) => !r.ok)
        .map((r) => ({ name: to.find((a) => a.id === r.id)?.name ?? "", error: r.error ?? "" }));
      if (bad.length) setFailed(bad);
      else close();
    }, errorText);
  }
  return (
    <Modal title={`رسالة واتساب إلى ${to.length} من المتقدمين`} open={open} onClose={close} wide>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        {failed && (
          <div className="alert" role="alert">
            لم تصل الرسالة إلى:
            <ul>
              {failed.map((f) => (
                <li key={f.name}>
                  {f.name}: {f.error}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="muted small">
          تُرسل من رقم واتساب الجمعية المربوط في الإعدادات، لكل متقدم على حدة:{" "}
          {to.map((a) => a.name).join("، ")}
        </p>
        <Field
          id="v-message"
          label="نص الرسالة"
          hint={`${VOLUNTEER_NAME_PLACEHOLDER} يُستبدل باسم كل متقدم. *نص* يظهر عريضًا في الواتساب.`}
        >
          <textarea
            id="v-message"
            rows={14}
            value={body}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
          />
        </Field>
        {missingLink && (
          <p className="small" role="status">
            ضع رابط القروب مكان «{GROUP_LINK}» قبل الإرسال.
          </p>
        )}
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || missingLink || !body.trim() || !to.length}>
            {busy ? "جارٍ الإرسال…" : `إرسال إلى ${to.length}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DecideModal({ decision, onClose }: { decision: Decision | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [note, setNote] = useState("");
  const interview = decision?.status === "interview";
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!decision) return;
    void run(async () => {
      await send("POST", `/v1/volunteer-applications/${decision.application.id}/decide`, {
        status: decision.status,
        note: note.trim() || undefined,
      });
      await qc.invalidateQueries({ queryKey: ["volunteers"] });
      setNote("");
      onClose();
    }, errorText);
  }
  return (
    <Modal
      title={`${interview ? "نقل إلى المقابلة" : "الاعتذار عن طلب"} ${decision?.application.name ?? ""}`}
      open={decision !== null}
      onClose={onClose}
    >
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field
          id="v-note"
          label={interview ? "موعد المقابلة أو ملاحظة (اختياري)" : "ملاحظة داخلية (اختياري)"}
          hint="لا تُرسل للمتقدم؛ تواصل معه عبر الواتساب"
        >
          <textarea id="v-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </Field>
        <div className="form-actions">
          <button className={interview ? "btn btn-primary" : "btn btn-danger"} disabled={busy}>
            {busy ? "جارٍ الحفظ…" : interview ? "نقل إلى المقابلة" : "اعتذار"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
